import React, { useRef, useState } from 'react';
import styles from './HATVQualificationActionModal.module.scss';
import { useTranslation } from 'react-i18next';
import dropdownArrowLight from '../../../assets/images/LightTheme/down_arrow_light.svg';
import dropdownArrowDark from '../../../assets/images/DarkTheme/down_arrow_dark.svg';
import { useAppSelector } from '../../../redux/hooks';
import { HATV_QUALIFICATION_ACTION } from '../../../constants/dataConstants';
import FormatterBar from '../../common/FormatterBar/FormaterBarWithLinkmodel/FormatterBar';
import Button from '../../common/Button/Button';
import useClickOutside from '../../CustomHooks/useClickOutside';
import {
  getFromLocalStorage,
  getTimePeriodOptionsTillCurrent,
  useProfileParams,
} from '../../../utils/common';
import { useUpdateQmonthApprovalStatusMutation } from '../../../redux/services/hatvQmonthDetailsAPI';
import { withUtag } from '../../../utils/analytics';
import i18n from '../../../i18n';

export interface IHATVQualificationActionProps {
  aboNumber: string;
  affiliateCode: string;
  approvedFor: string;
  approvalType: number;
  qualificationStatus: number;
  closingMonth?: number;
  onClose: () => void;
  setIsStatusUpdate?: (apiStatus: string) => void;
  currentAward?: string;
  currentStatus?: number;
  currentAwardCode?: string | number | null;
}

const HATVQualificationActionModal: React.FC<IHATVQualificationActionProps> = ({
  aboNumber,
  affiliateCode,
  approvedFor,
  approvalType,
  qualificationStatus,
  closingMonth,
  onClose,
  setIsStatusUpdate,
  currentAward,
  currentStatus,
  currentAwardCode,
}) => {
  const { t } = useTranslation();
  const fixedT = i18n.getFixedT('en'); // Gives always english
  const [selectedApprovalType, setSelectedApprovalType] = useState<number>(approvalType);
  const [timePeriod, setTimePeriod] = useState<number | undefined>(closingMonth);

  const [textareaValue, setTextareaValue] = useState<string>('');
  const [isEditable, setIsEditable] = useState(false);
  const [isNoteEditable, setNoteEditable] = useState(false);
  const [saveEnable, setSaveEnable] = useState(false);
  const [updateDisable, setUpdateDisable] = useState(false);

  const [updateQmonthApprovalApprovalStatus, { isLoading: isUpdating, isSuccess: isUpdated }] =
    useUpdateQmonthApprovalStatusMutation();
  const { aboNumber: urlParamAboNumber } = useProfileParams();
  const isDetailsPage = !!(urlParamAboNumber && urlParamAboNumber?.toString() === aboNumber);
  const textAreaChangeHandler = (editorData: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editorData, 'text/html');
    const rowNote = doc.body.textContent || '';
    if (rowNote && rowNote.length > 0) {
      setSaveEnable(true);
    } else {
      setSaveEnable(false);
    }
    editorData === '<p><br></p>' ? setTextareaValue('') : setTextareaValue(editorData);
  };

  const handleClickCancel = () => {
    setIsEditable(false);
    setNoteEditable(false);
    onClose();
  };

  function getStatusNameFromValue(code: number) {
    switch (code) {
      case 1:
        return 'Approve';
        break;
      case 2:
        return 'On Hold';
        break;
      case 9:
        return 'Deny';
        break;
    }
  }

  const currentApprovalStatus = currentStatus
    ? fixedT(`q_month_approval_status.${currentStatus}`)
    : '-';

  const handleClickSave = async () => {
    setUpdateDisable(true);
    updateQmonthApprovalApprovalStatus({
      aboNumber,
      affiliateCode,
      payload: {
        closingMonth: timePeriod?.toString() || '',
        approvalStatus: selectedApprovalType,
        note: textareaValue,
      },
    })
      .unwrap()
      .then(() => {
        setIsStatusUpdate && setIsStatusUpdate('success');
        setUpdateDisable(false);
        const currentAwardDetails = getFromLocalStorage<storedCurrentAwardDetails>('currentAward');
        const awardStatusTrackData = {
          event_name: 'award_status_change',
          abo_account: aboNumber,
          current_award: isDetailsPage
            ? currentAwardDetails?.currentAwardName || ''
            : currentAward || '',
          award_status: currentApprovalStatus,
          to_status: getStatusNameFromValue(selectedApprovalType),
          approval_type: isDetailsPage ? 'q_month_detail' : 'q_month_dashboard',
          am_country: localStorage?.getItem('userCountryCode') || '',
          am_affiliate: localStorage?.getItem('userAffiliateCode') || '',
          award_id: isDetailsPage
            ? currentAwardDetails?.currentAwardCode || ''
            : currentAwardCode || '',
        };
        console.log('HATV Q awardStatusTrackData >>>', awardStatusTrackData);
        withUtag().then((utag) => utag.link(awardStatusTrackData));
      })
      .catch((err) => {
        console.warn(' err :', err);
        setIsStatusUpdate && setIsStatusUpdate('fail');
        setUpdateDisable(false);
      });
  };

  return (
    <div className={`approval_container ${styles.approval_container}`}>
      <div className={`${styles.modal_title}`}>{t('update_qualification_status')}</div>
      <div className={`${styles.approvedForSection}`}>
        {t('update_qualification_status_for')}
        <span className={`${styles.approvedForText}`}>{` ${approvedFor}`}.</span>
      </div>
      <div className={styles.dropdown_container}>
        <ApprovalTypeDropdown
          selectedApprovalType={selectedApprovalType}
          qualificationStatus={qualificationStatus}
          onApprovalChange={setSelectedApprovalType}
        />
        <TimePeriodDropdown timePeriod={timePeriod} onTimePeriodChange={setTimePeriod} />
      </div>

      <div className={styles.button_input_wrapper}>
        <div className={styles.formatter_wrapper} data-testid="editor_testID">
          <FormatterBar
            id="notes"
            testId="notes_testID"
            editorData={textareaValue}
            handleDataChange={textAreaChangeHandler}
            section={'htvApproval'}
            placeholder={t('add_note_placeholder')}
            isContentEditable={isEditable}
            maxCharacter={1000}
          />
        </div>
        <div className={styles.btn_wrapper}>
          <Button
            id="cancelButton"
            onClick={handleClickCancel}
            label={t('cancel_btn')}
            style="unfilled"
            className={styles.btn_styles}
          />
          <Button
            id="saveButton"
            onClick={handleClickSave}
            label={t('update_status_btn')}
            style="filled"
            isDisabled={updateDisable || selectedApprovalType === null || timePeriod == null}
            className={styles.btn_styles}
          />
        </div>
      </div>
    </div>
  );
};

const ApprovalTypeDropdown = ({
  selectedApprovalType,
  qualificationStatus,
  onApprovalChange,
}: {
  selectedApprovalType: number;
  qualificationStatus: number;
  onApprovalChange: (type: number) => void;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const approvalDropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(approvalDropdownRef, () => setIsOpen(false));

  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow = currentTheme === 'light' ? dropdownArrowLight : dropdownArrowDark;

  const toggleDropdown = (e: React.MouseEvent) => {
    setIsOpen((isOpen) => !isOpen);
  };

  const handleApprovalChange = (value: number) => {
    onApprovalChange(value);
    setIsOpen(false);
  };
  return (
    <div
      className={`${styles.input_wrapper}`}
      data-testid="approve_toggle__testId"
      ref={approvalDropdownRef}
    >
      <input
        type="text"
        className={`approval_input ${styles.dropdown_input} ${isOpen ? 'selected_input' : ''}`}
        value={
          selectedApprovalType ? t(`hatv_q_month_approval_actions.${selectedApprovalType}`) : ''
        }
        readOnly
        data-testid="approve_dropdown_input_testId"
        placeholder={`${t('qualification_status_placeholder')}*`}
      />
      <img
        src={dropdownArrow}
        alt="Downkey"
        className={`${styles.arrow_icon} ${isOpen ? styles.rotate : ''}`}
        onClick={toggleDropdown}
      />
      {isOpen && (
        <div className={`dropdown_menu ${styles.dropdown_menu}`}>
          {Object.values(HATV_QUALIFICATION_ACTION).map((action) =>
            qualificationStatus !== action ? (
              <div
                key={action}
                className={`dropdown_item ${selectedApprovalType === action ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
                onClick={() => handleApprovalChange(action)}
              >
                {t(`hatv_q_month_approval_actions.${action}`)}
              </div>
            ) : null
          )}
        </div>
      )}
    </div>
  );
};

const TimePeriodDropdown = ({
  timePeriod,
  onTimePeriodChange,
}: {
  timePeriod: number | undefined;
  onTimePeriodChange: (type: number) => void;
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const month = timePeriod && parseInt(timePeriod.toString()?.slice(4, 6), 10) - 1;
  const timePeriodOptions = getTimePeriodOptionsTillCurrent(Number(month));
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setIsOpen(false));

  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dropdownArrow = currentTheme === 'light' ? dropdownArrowLight : dropdownArrowDark;

  const toggleDropdown = (e: React.MouseEvent) => {
    setIsOpen((isOpen) => !isOpen);
  };

  const handleTimePeriodChange = (value: number) => {
    onTimePeriodChange(value);
    setIsOpen(false);
  };
  return (
    <div
      className={`${styles.input_wrapper}`}
      data-testid="approve_toggle__testId"
      ref={dropdownRef}
    >
      <input
        type="text"
        className={`approval_input ${styles.dropdown_input} ${isOpen ? 'selected_input' : ''}`}
        value={timePeriodOptions.find((tp) => tp.value === timePeriod)?.label}
        readOnly
        data-testid="approve_dropdown_input_testId"
        placeholder={`${t('time_period_placeholder')}*`}
      />
      <img
        src={dropdownArrow}
        alt="Downkey"
        className={`${styles.arrow_icon} ${isOpen ? styles.rotate : ''}`}
        onClick={toggleDropdown}
      />
      {isOpen && (
        <div className={`dropdown_menu ${styles.dropdown_menu}`}>
          {timePeriodOptions.map(({ label, value }) => (
            <div
              key={value}
              className={`dropdown_item ${timePeriod === value ? 'dropdown_selected_item' : ''} ${styles.dropdown_item}`}
              onClick={() => handleTimePeriodChange(value)}
            >
              {label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default HATVQualificationActionModal;
